import { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';

// Validation schema for profile update
const ProfileSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  address: Yup.string(),
  phone: Yup.string()
    .matches(/^[0-9+\s()-]*$/, 'Invalid phone number format'),
});

// Validation schema for password change
const PasswordSchema = Yup.object().shape({
  currentPassword: Yup.string()
    .required('Current password is required'),
  newPassword: Yup.string()
    .required('New password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

function Profile() {
  const { user, updateProfile, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleProfileUpdate = async (values, { setSubmitting }) => {
    try {
      setError(null);
      setProfileSuccess(false);
      await updateProfile(values);
      setProfileSuccess(true);
      // Hide success message after 3 seconds
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordChange = async (values, { setSubmitting, resetForm }) => {
    try {
      setError(null);
      setPasswordSuccess(false);
      // This would be actually implemented in the AuthContext
      // await changePassword(values.currentPassword, values.newPassword);
      // console.log('Password change requested:', values);
      setPasswordSuccess(true);
      resetForm();
      // Hide success message after 3 seconds
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto mt-8 mb-12">
      <h1 className="text-3xl font-bold mb-6 text-hooray-red font-mont">Your Profile</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'profile'
                ? 'border-hooray-red text-hooray-red'
                : 'border-transparent text-gray-500 hover:text-hooray-red hover:border-hooray-red'
            }`}
          >
            Profile Information
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'password'
                ? 'border-hooray-red text-hooray-red'
                : 'border-transparent text-gray-500 hover:text-hooray-red hover:border-hooray-red'
            }`}
          >
            Change Password
          </button>
        </nav>
      </div>
      
      {/* Profile Information Form */}
      {activeTab === 'profile' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          {profileSuccess && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              Profile updated successfully!
            </div>
          )}
          
          <Formik
            initialValues={{
              name: user?.name || '',
              email: user?.email || '',
              address: user?.address || '',
              phone: user?.phone || '',
            }}
            validationSchema={ProfileSchema}
            onSubmit={handleProfileUpdate}
          >
            {({ isSubmitting, touched, errors }) => (
              <Form>
                <div className="mb-4">
                  <label htmlFor="name" className="block text-sm font-medium text-hooray-dark-text mb-1">
                    Full Name
                  </label>
                  <Field
                    type="text"
                    name="name"
                    id="name"
                    className={`w-full px-3 py-2 border rounded-md ${
                      touched.name && errors.name ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-hooray-red focus:border-transparent`}
                  />
                  <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-hooray-dark-text mb-1">
                    Email Address
                  </label>
                  <Field
                    type="email"
                    name="email"
                    id="email"
                    disabled={true}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 focus:outline-none cursor-not-allowed"
                  />
                  <p className="text-sm text-gray-500 mt-1">Email address cannot be changed</p>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="address" className="block text-sm font-medium text-hooray-dark-text mb-1">
                    Address (Optional)
                  </label>
                  <Field
                    as="textarea"
                    name="address"
                    id="address"
                    rows="3"
                    className={`w-full px-3 py-2 border rounded-md ${
                      touched.address && errors.address ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-hooray-red focus:border-transparent`}
                  />
                  <ErrorMessage name="address" component="div" className="text-red-500 text-sm mt-1" />
                </div>
                
                <div className="mb-6">
                  <label htmlFor="phone" className="block text-sm font-medium text-hooray-dark-text mb-1">
                    Phone Number (Optional)
                  </label>
                  <Field
                    type="text"
                    name="phone"
                    id="phone"
                    placeholder="+27 12 345 6789"
                    className={`w-full px-3 py-2 border rounded-md ${
                      touched.phone && errors.phone ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-hooray-red focus:border-transparent`}
                  />
                  <ErrorMessage name="phone" component="div" className="text-red-500 text-sm mt-1" />
                </div>
                
                <Button
                  type="submit"
                  variant="cta"
                  disabled={isSubmitting || loading}
                  className="w-full"
                >
                  {isSubmitting || loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      )}
      
      {/* Change Password Form */}
      {activeTab === 'password' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          {passwordSuccess && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              Password changed successfully!
            </div>
          )}
          
          <Formik
            initialValues={{ currentPassword: '', newPassword: '', confirmPassword: '' }}
            validationSchema={PasswordSchema}
            onSubmit={handlePasswordChange}
          >
            {({ isSubmitting, touched, errors }) => (
              <Form>
                <div className="mb-4">
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-hooray-dark-text mb-1">
                    Current Password
                  </label>
                  <Field
                    type="password"
                    name="currentPassword"
                    id="currentPassword"
                    className={`w-full px-3 py-2 border rounded-md ${
                      touched.currentPassword && errors.currentPassword ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-hooray-red focus:border-transparent`}
                  />
                  <ErrorMessage name="currentPassword" component="div" className="text-red-500 text-sm mt-1" />
                </div>
                
                <div className="mb-4">
                  <label htmlFor="newPassword" className="block text-sm font-medium text-hooray-dark-text mb-1">
                    New Password
                  </label>
                  <Field
                    type="password"
                    name="newPassword"
                    id="newPassword"
                    className={`w-full px-3 py-2 border rounded-md ${
                      touched.newPassword && errors.newPassword ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-hooray-red focus:border-transparent`}
                  />
                  <ErrorMessage name="newPassword" component="div" className="text-red-500 text-sm mt-1" />
                </div>
                
                <div className="mb-6">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-hooray-dark-text mb-1">
                    Confirm New Password
                  </label>
                  <Field
                    type="password"
                    name="confirmPassword"
                    id="confirmPassword"
                    className={`w-full px-3 py-2 border rounded-md ${
                      touched.confirmPassword && errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-hooray-red focus:border-transparent`}
                  />
                  <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-sm mt-1" />
                </div>
                
                <Button
                  type="submit"
                  variant="cta"
                  disabled={isSubmitting || loading}
                  className="w-full"
                >
                  {isSubmitting || loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Changing Password...
                    </span>
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
}

export default Profile; 